// --------------------------
// IMPORTS
// --------------------------
import { Row, Col, Form, DatePicker, Input, message, Table, Modal } from "antd";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Select from "react-select";
import Column from "antd/es/table/Column";
import CustomLoader from "../../utils/CustomLoader";
import useCurrentPath, {
  decryptData,
  draggerProps,
  getRateByDate,
} from "../../utils/Constants";
import {
  addOrDraftExpenseThunk,
  getAdminApproverListThunk,
  getBlockedDateThunk,
} from "../../store/thunks/addNewExpenseThunk";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import AddNewButton from "../../components/atoms/Buttons/AddNewButton";
import media from "../../media";
import Dragger from "antd/es/upload/Dragger";

import {
  getCurrencyListThunk,
  getDepartmentListThunk,
  getExpenseCodeListThunk,
  getProjectCodeListThunk,
  getUserListThunk,
  getVendorListThunk,
} from "../../store/thunks/masterDataThunk";

// --------------------------
// MAIN COMPONENT
// --------------------------
export default function AddExpense({ isVisible, setIsVisible }) {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentPath = useCurrentPath();
  const { RangePicker } = DatePicker;
  const [form] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  let isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);
  let storeUserInfo = useSelector((state) => state.auth.userInfo);
  storeUserInfo = decryptData(storeUserInfo);
  const storeEmpID = storeUserInfo?.user?.empID;
  const storeEmpName = storeUserInfo?.user?.name;

  const storeEmpDepartmentID = storeUserInfo?.user?.departmentID;
  const storeEmpDepartmentName = storeUserInfo?.userDepartment;
  const storeEmpDepartmentCode = storeUserInfo?.userDepartmentCode;
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formAction, setFormAction] = useState("");
  const [currencyRates, setCurrencyRates] = useState({});
  const [blockedDate, setBlockedDate] = useState(null);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState([]);
  const [users, setUsers] = useState([]);
  const [adminApprovers, setAdminApprovers] = useState([]);
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [natureOfExpense, setNatureOfExpense] = useState([
    { label: "Already Paid - Filling", value: "Already Paid - Filling" },
    { label: "Reimbursement", value: "Reimbursement" },
    { label: "Travel Settlement", value: "Travel Settlement" },
    { label: "Vendor Bill", value: "Vendor Bill" },
  ]);
  const [addExpenseValues, setAddExpenseValues] = useState(null);
  const [expenseValues, setExpenseValues] = useState({
    empName: "",
    empID: "",
    departmentID: "",
    expensePeriodFromDate: "",
    expensePeriodToDate: "",
    expenseEmpIDRole: "",
    businessPurpose: "",
    natureOfExpense: "",
    expensesDetails: "",
    subTotal: "",
    totalReimbursement: "",
  });  
  const [rows, setRows] = useState([
    {
      date: "",
      description: "",
      departmentID: "",
      expenseCodeID: "",
      currencyID: "",
      recharge: "No",
      cost: "",
      mediaInfo: [],
    },
  ]);
  const [rechargeOpts, setRechargeOpts] = useState([
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ]);
  const [mediaInfoRows, setMediaInfoRows] = useState([
    {
      files: [],
    },
  ]);
  const [loading, setLoading] = useState(false);

  const parseToFloat = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const subTotal = rows.reduce(
    (total, row) => total + parseToFloat(row.cost),
    0
  );

  //const totalReimbursement = subTotal.toFixed(2);
  const initialTotalReimbursement = 0;
  const [totalReimbursement, setTotalReimbursement] = useState(
    initialTotalReimbursement.toFixed(2)
  );  

  // --------------------------
  // API CALLS
  // --------------------------

  const expCal = () => {
    const payload = {
      role: storeRole,
      lIUId: storeLIUId,
    };

    dispatch(getBlockedDateThunk(payload))
      .unwrap()
      .then((response) => {
        if (response?.expCalRes) {
          response.expCalRes?.blockedOn &&
          response.expCalRes?.status === process.env.REACT_APP_BLOCK_STATUS
            ? setBlockedDate(response.expCalRes)
            : setBlockedDate(null);
        }
      });
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    expCal();
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      // Run the function immediately
      if (storeRole !== process.env.REACT_APP_SROLE) {
        expCal();
      }
      const intervalId = setInterval(() => {
        if (storeRole !== process.env.REACT_APP_SROLE) {
          expCal();
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [storeRole, storeToken, isLoggedIn]);

  useEffect(() => {
    if (storeEmpID) {
      const currEmpID = parseInt(storeEmpID);
      const emp = users.find((emp) => emp.empID === currEmpID);

      const expenseEmpIDRole = emp ? emp.roleID?.name : "";

      setExpenseValues({
        ...expenseValues,
        expenseEmpIDRole: expenseEmpIDRole,
      });
      form.setFieldsValue({ empID: storeEmpID });
      form.setFieldsValue({ empName: storeEmpName });
      form.setFieldsValue({
        departmentID: `${storeEmpDepartmentName} - ${storeEmpDepartmentCode}`,
      });
    }
  }, []);

  useEffect(() => {    
    const requests = [
      dispatch(getCurrencyListThunk()).unwrap(),
      dispatch(getDepartmentListThunk()).unwrap(),
      dispatch(getVendorListThunk()).unwrap(),
      dispatch(getExpenseCodeListThunk()).unwrap(),
      dispatch(getProjectCodeListThunk()).unwrap(),
      dispatch(getUserListThunk()).unwrap(),
    ];

    if (storeRole === process.env.REACT_APP_ADROLE) {
      requests.push(
        dispatch(
          getAdminApproverListThunk({
            category: process.env.REACT_APP_EXPENSE_CATEGORY_CAPEX,
          })
        ).unwrap()
      );
    }

    Promise.all(requests)
      .then(
        ([
          currencyRes,
          departments,
          vendors,
          expenseCodes,
          projectCodes,
          users,
          adminApprovers,
        ]) => {
          setAllCurrencies(currencyRes.currencyDropdown || []);
          setCurrencyRates(currencyRes.currencyRates || []);
          setDepartments(departments || []);
          setVendors(vendors || []);
          setExpenseCodes(expenseCodes || []);
          setProjectCodes(projectCodes || []);
          setUsers(users || []);
          if (adminApprovers) setAdminApprovers(adminApprovers || []);
        }
      )
      .finally(() => setShowLoader(false));
  }, [currentPath, dispatch, storeRole]);

  // --------------------------
  // HANDLERS
  // --------------------------

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Add Expense API
  const onFinish = (values) => {
    if (errors?.fileSizeExceed === "single") {
      message.error("File size exceeds 5 MB.");
    } else if (errors?.fileSizeExceed === "total") {
      message.error("The total size of all files must not exceed 10 MB.");
    } else if (errors?.fileCountExceed) {
      message.error(
        "You cannot only upload one attachment so upload accordingly."
      );
    } else if (totalReimbursement.indexOf("-") !== -1) {
      message.error(
        totalReimbursement +
          " amount is with you. If you have any concern then please contact with Finance Department!!"
      );
    } else {
      setAddExpenseValues(values);
      setIsModalVisible(true);
    }
  };

  const handleOk = () => {
    if (!addExpenseValues) return;
    setIsModalVisible(false);
    setShowLoader(true);
    setErrors({ fileSizeExceed: "", fileCountExceed: false });

    const payload = {
      route: process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN,
      empName: addExpenseValues.empName,
      empID: parseInt(storeEmpID),
      departmentID: storeEmpDepartmentID,
      expensePeriodFromDate: expenseValues?.expensePeriodFromDate,
      expensePeriodToDate: expenseValues?.expensePeriodToDate,
      businessPurpose: addExpenseValues?.businessPurpose?.label,
      natureOfExpense: addExpenseValues?.natureOfExpense?.value,
      projectCodeID: addExpenseValues?.projectCodeID?.value,
      expensesDetails: rows,
      historySource: "portal",
      historyAction: formAction === "draft" ? "save as draft" : "create",
      historyUserID: storeLIUId,
      status:
        formAction === "draft"
          ? process.env.REACT_APP_DRAFT_STATUS.toUpperCase()
          : process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
      isDrafted: process.env.REACT_APP_DRAFT_STATUS.toUpperCase(),
    };

    if (adminApprovers.length > 0) {
      payload.adminSelectedApprover = expenseValues?.approvers?.value;
    }

    dispatch(addOrDraftExpenseThunk(payload))
      .then(() => {
        message.success(
          formAction === process.env.REACT_APP_DRAFT_STATUS.toLowerCase()
            ? "Expense drafted successfully."
            : "Expense added successfully."
        );
        form.resetFields();
        form.setFieldsValue({
          empID: storeEmpID,
          empName: storeEmpName,
        });
        setExpenseValues({ ...expenseValues, advanceAmount: "" });
        setRows([
          {
            date: "",
            description: "",
            departmentID: "",
            expenseCodeID: "",
            currencyID: "",
            recharge: "No",
            //currencyRateDateTime: "",
            cost: "",
            mediaInfo: [],
          },
        ]); // Reset rows to initial state
        setShowLoader(false);
        navigate(`/${process.env.REACT_APP_DASHBOARD_EL_SEARCH_IN}`);
      })
      .catch((error) => {
        setShowLoader(false);
      });
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setShowLoader(false);
  };

  const handleAddRow = () => {
    const newIndex = rows.length + 1;
    setRows([
      ...rows,
      {
        title: `Expense Detail #${newIndex}`,
        date: "",
        description: "",
        departmentID: "",
        expenseCodeID: "",
        currencyID: "",
        cost: "",
        mediaInfo: [],
      },
    ]);
  };

  const handleChange = (index, field, value) => {
    const updatedRows = [...rows];

    if (
      field === "departmentID" ||
      field === "expenseCodeID" ||
      field === "currencyID" ||
      field === "recharge"
    ) {
      updatedRows[index][field] = value?.value;
    } else {
      updatedRows[index][field] = value;
    }

    const currentRow = updatedRows[index];
    const { date, currencyID, cost } = currentRow;

    const costValue = parseFloat(cost || 0);

    // Only process if both date and currency are selected
    if (date && currencyID) {
      const matched = getRateByDate(currencyRates, currencyID, date);

      const rate = matched?.rate || 1;
      const rateDateTime = matched?.effectiveDateTime || null;

      updatedRows[index].currencyRate = rate;
      //updatedRows[index].currencyRateDateTime = rateDateTime;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    } else {
      // If not all values selected, just default costInINR = cost
      const rate = currencyRates[currencyID]?.[0]?.rate || 1;
      updatedRows[index].costInINR = (costValue * rate).toFixed(2);
    }

    // Recalculate total reimbursement
    const total = updatedRows.reduce((sum, r) => {
      const val = parseFloat(r.costInINR || 0);
      return sum + (isNaN(val) ? 0 : val);
    }, 0);

    setRows(updatedRows);
    setTotalReimbursement(total.toFixed(2));
  };

  const handleRemoveRow = (indexToRemove) => {
    const updatedRows =
      rows && rows.filter((row, index) => index !== indexToRemove);
    setRows(updatedRows);
    // Convert date fields back to moment objects if they exist
    const updatedFields =
      updatedRows &&
      updatedRows.map((row) => ({
        ...row,
        date: row.date ? moment(row.date) : null,
      }));

    form.setFieldsValue({ rows: updatedFields });
  };

  const handleFileChange = (index, fileOrEvent) => {
    // Handle both Dragger (file object) and Browse File (event)
    const files = fileOrEvent?.target?.files || [fileOrEvent]; // auto-detect source

    if (files.length > 1) {
      setErrors({ fileCountExceed: true });
      message.error("Only one file can be uploaded.");
      return;
    }

    setErrors({ fileCountExceed: false });

    const maxSingleFileSize = 4 * 1024 * 1024; // 4MB
    const file = files[0];
    const updatedRows = [...rows];

    // File size check
    if (file.size > maxSingleFileSize) {
      setErrors({ fileSizeExceed: "single" });
      message.error(`File "${file.name}" exceeds 4MB.`);
      return;
    }

    // File type check
    const allowedTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed!");
      return;
    }

    const reader = new FileReader();
    const fileSources = [];

    const arrayBufferToBase64 = (buffer) => {
      let binary = "";
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return window.btoa(binary);
    };

    reader.onload = (e) => {
      let fileData;
      if (file.type.startsWith("image/")) {
        fileData = e.target.result;
      } else {
        const base64Data = arrayBufferToBase64(e.target.result);
        fileData = `data:${file.type};base64,${base64Data}`;
      }

      fileSources.push({
        src: fileData,
        name: file.name,
        type: file.type,
      });

      updateRowFiles(index, fileSources, updatedRows);
    };

    // Read file accordingly
    if (file.type.startsWith("image/")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleFileChange1 = (index, event) => {
    const files = event.target.files;
    if (files.length && files.length > 1) {
      setErrors({ fileCountExceed: true });
      message.error(
        `Only one file can be uploaded. Allowed file types: .jpg, .png, .pdf maximum size: 4MB.`
      );
    } else {
      setErrors({ fileCountExceed: false });

      let totalSize = 0;
      const maxTotalSize = 10 * 1024 * 1024; // 10 MB in bytes
      const maxSingleFileSize = 4 * 1024 * 1024; // 5 MB in bytes
      const fileSources = [];
      const updatedRows = [...rows];

      const arrayBufferToBase64 = (buffer) => {
        let binary = "";
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
      };

      const loadFile = (file) => {
        const reader = new FileReader();
        const fileType = file.type;
        totalSize += file.size;

        if (file.size > maxSingleFileSize) {
          setErrors({ fileSizeExceed: "single" });
          message.error(`File "${file.name}" exceeds 4MB.\n`);
        } else {
          reader.onload = (e) => {
            if (fileType.startsWith("image/")) {
              // Directly use the base64 data URL for images
              fileSources.push({
                src: e.target.result,
                name: file.name,
                type: fileType,
              });
            } else {
              // Convert ArrayBuffer to base64 for non-image files
              const base64Data = arrayBufferToBase64(e.target.result);
              fileSources.push({
                src: `data:${fileType};base64,${base64Data}`,
                name: file.name,
                type: fileType,
              });
            }

            if (fileSources.length <= 1) {
              updateRowFiles(index, fileSources, updatedRows);
            }
          };

          if (fileType.startsWith("image/")) {
            reader.readAsDataURL(file); // For images
          } else {
            reader.readAsArrayBuffer(file); // For non-image files
          }
        }
      };
      Array.from(files).forEach(loadFile);
    }
  };

  const updateRowFiles = (index, fileSources, updatedRows) => {
    if (!updatedRows[index].mediaInfo) {
      updatedRows[index].mediaInfo = [];
    }
    updatedRows[index].mediaInfo = fileSources;
    setRows(updatedRows);
  };

  const validationRules = {
    date: [{ required: true, message: "Please select a date" }],
    description: [{ required: true, message: "Please enter a description" }],
    departmentID: [{ required: true, message: "Please select a department" }],
    expenseCodeID: [
      { required: true, message: "Please select an expense code" },
    ],
    currencyID: [{ required: true, message: "Please enter a currency" }],
    recharge: [{ required: true, message: "Please select recharge option" }],
    cost: [
      { required: true, message: "Please enter a cost" },
      {
        pattern: /^[0-9]*\.?[0-9]+$/,
        message: "Only numbers and decimals are allowed",
      },
    ],
    upload: [{ required: true, message: "Please select a file" }],
  };

  const handleButtonClick = (index) => {
    document.getElementById(`fileInput${index}`).click();
  };

  // Disable dates after the current date
  const disableFutureDates = (current) => {
    const startDate = moment(current).subtract(1, "month").startOf("day");

    // Calculate the end date for the next financial year
    const currentYear = moment().year();
    const nextFinancialYearEnd = moment()
      .month(2)
      .date(31)
      .year(
        currentYear + (moment().isAfter(moment().month(2).date(31)) ? 1 : 0)
      )
      .endOf("day");

    // Disable dates outside the range two months ago from today to next financial year's 31st March
    return (
      current &&
      (current.isBefore(startDate) || current.isAfter(moment().endOf("day")))
    );
  };

  const validateFilePresence = (_, value) => {
    return rows.some((row) => row.mediaInfo && row.mediaInfo.length > 0)
      ? Promise.resolve()
      : Promise.reject(new Error("Please upload a file!"));
  };

  function getMinDate(dateStr) {
    const date = new Date(dateStr); // e.g., "2025-05-22"
    const year = date.getFullYear();
    const month = date.getMonth(); // Current month (0-indexed)

    // Set to 0th day of current month to get last day of previous month
    return new Date(year, month, 0); // e.g., 30 April 2025
  }

  // Preprocess rows to add a stable 'id' if missing
  const rowsWithIds = rows && rows.map((row, idx) => ({ ...row, id: idx }));

  //const formattedDate = new Date().toISOString().slice(0, 16).replace("T", " ");
  const formattedDate = new Date()
    .toLocaleString("sv-SE", { hour12: false })
    .replace("T", " ")
    .slice(0, 16);

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <>      
      {isVisible && (
        <div className="addExpensePopupForm">
          <div className="addExpensePopupFormWraper">
            {showLoader ? <CustomLoader showLoader={showLoader} />:
              <>
                <div className="expenseDetailTitle contentBottomPadding">
                  <h3 className="text-dark font-size-lg">Add New Expense</h3>
                  <button
                    onClick={() => setIsVisible(false)}
                    className="expenseDetailTitleClose"
                  >
                    <img src={media.popupCross.svg} alt="close" />
                  </button>
                </div>
                <div className="customExpenseLabelForm">
                  <Form
                    layout="vertical"
                    autoComplete="off"
                    className="form-validation-start"
                    onFinish={onFinish}
                    form={form}
                    onFinishFailed={onFinishFailed}
                    initialValues={{ rows: [{ mediaInfo: [] }] }}
                  >
                    <Row className="addMainWrap" gutter={[18, 24]}>
                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="empID"
                          name="empID"
                          rules={[
                            {
                              pattern: /^[0-9]*\.?[0-9]+$/,
                              message:
                                "Alpbhabets and special symbols not allowing in Employee ID",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="empID"
                              className="block mb-1 font-medium"
                            >
                              Employee ID
                            </label>
                            <Input
                              className="form-control h-45px rounded-md"
                              //value={expenseValues?.empID}
                              value={storeEmpID}
                              disabled={storeEmpID !== 1 ? "disabled" : ""}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="empName"
                          name="empName"
                          rules={[
                            {
                              pattern: /^[^\d]*$/,
                              message:
                                "Numbers are not allowed in the employee name",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="empName"
                              className="block mb-1 font-medium"
                            >
                              Employee Name
                            </label>
                            <Input
                              className="form-control h-45px rounded-md"
                              value={storeEmpName}
                              disabled
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="departmentID"
                          name="departmentID"
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="departmentID"
                              className="block mb-1 font-medium"
                            >
                              Department
                            </label>
                            <Input
                              className="form-control h-45px rounded-md"
                              value={`${storeEmpDepartmentName} - ${storeEmpDepartmentCode}`}
                              disabled="disabled"
                              placeholder="Select Department"
                            />
                          </div>

                          {/* <Select
                          allowClear
                          className="rounded-md h-45px"
                          placeholder="Select Department"
                          options={departments}
                          value={expenseValues?.departmentID}
                          onChange={(selectedOption) =>
                            setExpenseValues({
                              ...expenseValues,
                              departmentID: selectedOption,
                            })
                          }
                        /> */}
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="expensePeriodFromDate"
                          name="expensePeriodFromDate"
                          rules={[
                            {
                              required: true,
                              message: "Please enter Expense Period: (From - To)",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="expensePeriodFromDate"
                              className="block mb-1 font-medium"
                            >
                              Expense Period: (From - To)
                              <span className="mandatory-symbol">*</span>
                            </label>

                            <RangePicker
                              className="form-control h-45px rounded-md d-inline-flex"
                              format="DD-MM-YYYY"
                              //disabledDate={disableFutureDates}
                              disabledDate={(current) => {
                                const minDate = new Date(blockedDate?.blockedOn);
                                return (
                                  current && current.toDate() <= getMinDate(minDate)
                                );
                              }}
                              value={
                                expenseValues.expensePeriodFromDate &&
                                expenseValues.expensePeriodToDate
                                  ? [
                                      moment(
                                        expenseValues.expensePeriodFromDate,
                                        "YYYY-MM-DD"
                                      ),
                                      moment(
                                        expenseValues.expensePeriodToDate,
                                        "YYYY-MM-DD"
                                      ),
                                    ]
                                  : null
                              }
                              onChange={(date, dates) => {
                                const formattedDates =
                                  dates &&
                                  dates.map((date) =>
                                    moment(date, "DD-MM-YYYY").format("YYYY-MM-DD")
                                  );
                                if (
                                  formattedDates &&
                                  formattedDates[0] &&
                                  formattedDates[1]
                                ) {
                                  form.setFieldsValue({
                                    expensePeriodFromDate: formattedDates,
                                  });
                                  setExpenseValues({
                                    ...expenseValues,
                                    expensePeriodFromDate: formattedDates[0],
                                    expensePeriodToDate: formattedDates[1],
                                  });
                                }
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="projectCodeID"
                          name="projectCodeID"
                          rules={[
                            {
                              required: true,
                              message: "Please select project code",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="projectCodeID"
                              className="block mb-1 font-medium"
                            >
                              Project Code
                              <span className="mandatory-symbol">*</span>
                            </label>
                            <Select
                              className="h-45px lh-1Pt5 rounded-md"
                              onChange={(value) => {
                                form.setFieldsValue({ projectCodeID: value });
                                setExpenseValues({
                                  ...expenseValues,
                                  projectCodeID: value,
                                });
                              }}
                              value={form.getFieldValue("projectCodeID")}
                              options={projectCodes}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                      {storeRole === process.env.REACT_APP_ADROLE && (
                        <Col xs={24} md={12} xl={12}>
                          <Form.Item
                            className="m-0"
                            key="approvers"
                            name="approvers"
                            rules={[
                              {
                                required: true,
                                message: "Please select approver",
                              },
                            ]}
                          >
                            <div className="form-row-wrap">
                              <label
                                htmlFor="approvers"
                                className="block mb-1 font-medium"
                              >
                                Approvers<span className="mandatory-symbol">*</span>
                              </label>
                              <Select
                                className="h-45px lh-1Pt5 rounded-md"
                                onChange={(value) => {
                                  form.setFieldsValue({ approvers: value });
                                  setExpenseValues({
                                    ...expenseValues,
                                    approvers: value,
                                  });
                                }}
                                options={adminApprovers}
                                value={form.getFieldValue("approvers")}
                              />
                            </div>
                          </Form.Item>
                        </Col>
                      )}
                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="natureOfExpense"
                          name="natureOfExpense"
                          rules={[
                            {
                              required: true,
                              message: "Nature of Expense is Mandatory",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="natureOfExpense"
                              className="block mb-1 font-medium"
                            >
                              Nature of Expense
                              <span className="mandatory-symbol">*</span>
                            </label>
                            <Select
                              className="h-45px lh-1Pt5 rounded-md"
                              allowClear
                              options={natureOfExpense}
                              value={form.getFieldValue("natureOfExpense")}
                              onChange={(value) => {
                                form.setFieldsValue({ natureOfExpense: value });
                                setExpenseValues({
                                  ...expenseValues,
                                  natureOfExpense: value,
                                });
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12} xl={12}>
                        <Form.Item
                          className="m-0"
                          key="businessPurpose"
                          name="businessPurpose"
                          rules={[
                            {
                              required: true,
                              message: "Please enter vendor name",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="businessPurpose"
                              className="block mb-1 font-medium"
                            >
                              Vendor Name
                              <span className="mandatory-symbol">*</span>
                            </label>
                            <Select
                              className="h-45px lh-1Pt5 rounded-md"
                              allowClear
                              options={vendors}
                              value={form.getFieldValue("businessPurpose")}
                              onChange={(value) => {
                                form.setFieldsValue({ businessPurpose: value });
                                setExpenseValues({
                                  ...expenseValues,
                                  businessPurpose: value,
                                });
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                      {/* <Col sm={24}>
                        <Form.Item
                          className="m-0"
                          key="businessPurpose"
                          name="businessPurpose"
                          rules={[
                            {
                              required: true,
                              message: "Please enter vendor name",
                            },
                          ]}
                        >
                          <div className="form-row-wrap">
                            <label
                              htmlFor="businessPurpose"
                              className="block mb-1 font-medium"
                            >
                              Vendor Name<span className="mandatory-symbol">*</span>
                            </label>
                            <TextArea
                              placeholder="Enter the vendor name"
                              className="form-control rounded-md"
                              autoSize={{
                                minRows: 2,
                                maxRows: 4,
                              }}
                              onChange={(e) => {
                                setExpenseValues({
                                  ...expenseValues,
                                  businessPurpose: e.target.value,
                                });
                              }}
                            />
                          </div>
                        </Form.Item>
                      </Col> */}
                    </Row>
                    <div className="main-repeater expenseAddRepeater">
                      <div className="addExpenseSubGroup">
                        <h3>Add Expense Details</h3>
                        <AddNewButton
                          addNewButtonClassName="addMoreBtn"
                          addNewButtonName="Add More"
                          btnType="button"
                          onClick={handleAddRow}
                          setShowLoader={setShowLoader}
                        />
                      </div>
                      <Table
                        className="table"
                        dataSource={rowsWithIds}
                        rowKey="id"
                        pagination={false}
                        showHeader={false}
                      >
                        <Column
                          title="Expense Details"
                          key="expenseDetails"
                          render={(text, record, index) => (
                            <div className="flex flex-col gap-2">
                              <h3 className="text-dark font-size-lg expenseRepeaterDetailHeading">
                                {record.title || `Expense Detail #${index + 1}`}
                              </h3>
                              {/* Stack form items vertically */}
                              <Row gutter={[18, 12]}>
                                {/* Date */}
                                <Col xs={24} md={12} xl={12}>
                                  <Form.Item
                                    name={["rows", index, "date"]}
                                    rules={validationRules.date}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        for="date"
                                        class="block mb-1 font-medium"
                                      >
                                        Date<span class="mandatory-symbol">*</span>
                                      </label>
                                      <DatePicker
                                        disabledDate={(current) => {
                                          const minDate = new Date(
                                            blockedDate?.blockedOn
                                          );
                                          return (
                                            current &&
                                            current.toDate() <= getMinDate(minDate)
                                          );
                                        }}
                                        format="DD-MM-YYYY"
                                        // value={
                                        //   record?.date ? moment(record?.date) : null
                                        // }
                                        className="form-control h-45px rounded-md d-inline-flex"
                                        placeholder="Select Date"
                                        onChange={(date) => {
                                          const formattedDate = date
                                            ? date.format("YYYY-MM-DD")
                                            : null;
                                          handleChange(
                                            index,
                                            "date",
                                            formattedDate
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "date"],
                                            formattedDate
                                          );
                                        }}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Description */}
                                  <Form.Item
                                    name={["rows", index, "description"]}
                                    rules={validationRules.description}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        for="description"
                                        class="block mb-1 font-medium"
                                      >
                                        Description
                                        <span class="mandatory-symbol">*</span>
                                      </label>
                                      <Input
                                        placeholder="Description"
                                        value={record.description}
                                        className="form-control h-45px rounded-md"
                                        onChange={(e) =>
                                          handleChange(
                                            index,
                                            "description",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Department */}
                                  <Form.Item
                                    name={["rows", index, "departmentID"]}
                                    rules={validationRules.departmentID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`departmentID-${index}`}
                                        class="block mb-1 font-medium"
                                      >
                                        Department
                                        <span class="mandatory-symbol">*</span>
                                      </label>
                                      <Select
                                        id={`departmentID-${index}`}
                                        //value={record.departmentID}
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "departmentID",
                                            value
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "departmentID"],
                                            value
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                        options={departments}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Expense Code */}
                                  <Form.Item
                                    name={["rows", index, "expenseCodeID"]}
                                    rules={validationRules.expenseCodeID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`expenseCode-${index}`}
                                        class="block mb-1 font-medium"
                                      >
                                        Expense Code
                                        <span class="mandatory-symbol">*</span>
                                      </label>
                                      <Select
                                        id={`expenseCode-${index}`}
                                        //value={record.expenseCodeID}
                                        onChange={(value) => {
                                          handleChange(
                                            index,
                                            "expenseCodeID",
                                            value
                                          );
                                          form.setFieldValue(
                                            ["rows", index, "expenseCodeID"],
                                            value
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                        options={expenseCodes}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Currency */}
                                  <Form.Item
                                    name={["rows", index, "currencyID"]}
                                    rules={validationRules.currencyID}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`currencyID-${index}`}
                                        class="block mb-1 font-medium"
                                      >
                                        Currency
                                        <span class="mandatory-symbol">*</span>
                                      </label>
                                      <Select
                                        id={`currencyID-${index}`}
                                        //value={record.currencyID}
                                        onChange={(value) => {
                                          handleChange(index, "currencyID", value);
                                          form.setFieldValue(
                                            ["rows", index, "currencyID"],
                                            value
                                          );
                                        }}
                                        className="h-45px rounded-md"
                                        options={allCurrencies}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>
                                <Col xs={24} md={12} xl={12}>
                                  {/* Recharge */}
                                  <Form.Item
                                    name={["rows", index, "recharge"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: "Please select recharge option",
                                      },
                                    ]}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        htmlFor={`recharge-${index}`}
                                        className="block mb-1 font-medium"
                                      >
                                        Recharge
                                        <span className="mandatory-symbol">*</span>
                                      </label>
                                      <Select
                                        id={`recharge-${index}`}
                                        className="h-45px rounded-md"
                                        options={rechargeOpts}
                                        value={rechargeOpts.find(
                                          (opt) =>
                                            opt.value ===
                                            form.getFieldValue([
                                              "rows",
                                              index,
                                              "recharge",
                                            ])
                                        )}
                                        onChange={(value) => {
                                          handleChange(index, "recharge", value);
                                          form.setFieldValue(
                                            ["rows", index, "recharge"],
                                            value
                                          );
                                        }}
                                        getOptionLabel={(option) => option.label}
                                        getOptionValue={(option) => option.value}
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>

                                <Col xs={24} md={24} xl={24}>
                                  {/* Cost */}
                                  <Form.Item
                                    name={["rows", index, "cost"]}
                                    rules={validationRules.cost}
                                  >
                                    <div className="form-row-wrap">
                                      <label
                                        for="cost"
                                        class="block mb-1 font-medium"
                                      >
                                        Cost<span class="mandatory-symbol">*</span>
                                      </label>
                                      <Input
                                        placeholder="Enter cost"
                                        type="text"
                                        value={record.cost}
                                        className="form-control h-45px rounded-md"
                                        onChange={(e) =>
                                          handleChange(
                                            index,
                                            "cost",
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>
                                  </Form.Item>
                                </Col>

                                <Col xs={24} md={12} xl={12}>
                                  {/* Upload
            <Form.Item className="uploadNotification" label="Upload">
              <input
                id={`fileInput${index}`}
                type="file"
                accept=".png, .jpg, .jpeg, .pdf, .docx, .doc"
                onChange={(event) => handleFileChange(index, event)}
                style={{ display: "none" }}
              />
              <SubmitButton
                ky="upload"
                type="primary"
                submitName="Upload"
                className="btn mx-auto d-block defaultbutton rounded-lg text-white"
                onClick={() => handleButtonClick(index)}
              />
              {record.mediaInfo?.length ? (
                <Typography.Text>
                  {record.mediaInfo.length} file(s) attached
                </Typography.Text>
              ) : (
                <img
                  src={media.notification.svg}
                  alt="Upload info"
                  title="Allowed: jpg, png, pdf, docx (max 4MB)"
                />
              )}

      </Form.Item>
    */}
                                  <Form.Item name={["rows", index, "media"]}>
                                    <Dragger
                                      className="uploadDrag"
                                      {...draggerProps(setLoading)}
                                      customRequest={({ file }) =>
                                        handleFileChange(index, file)
                                      }
                                    >
                                      <p className="ant-upload-drag-icon">
                                        <img src={media.upload.svg} />
                                      </p>
                                      <p className="ant-upload-text">
                                        Drag your file(s) to start uploading
                                      </p>
                                      <p className="ant-upload-hint">OR</p>
                                      <span className="uploadBtn">
                                        <img
                                          src={media.uploadIcon.svg}
                                          alt="Upload info"
                                          title="Allowed: jpg, png, pdf, docx (max 4MB)"
                                        />{" "}
                                        Browse file
                                      </span>
                                    </Dragger>
                                  </Form.Item>
                                </Col>

                                {/* Remove Row (optional) */}
                                {rows?.length > 1 && (
                                  <i
                                    className="flaticon-delete icon-lg cursor-pointer"
                                    onClick={() => handleRemoveRow(index)}
                                  ></i>
                                )}
                              </Row>
                            </div>
                          )}
                        />
                      </Table>
                    </div>
                    <Row gutter={[24, 24]}>
                      <Col xs={24}>
                        <div className="my-5"></div>
                      </Col>
                    </Row>
                    <div className="text-center footerBtn">
                      <div className="footerReversment">
                        <small>Total Reimbursement</small>
                        <h4>
                          {process.env.REACT_APP_DEFAULT_CURRENCY}
                          {totalReimbursement}
                        </h4>
                      </div>
                      <div className="btnsWrap">
                        <SubmitButton
                          ky="saveAsDraft"
                          //type="primary"
                          htmlType="submit"
                          submitName="Save as draft"
                          className="defalutWhiteBtn"
                          onClick={() => {
                            setFormAction("draft");
                          }}
                          loading={false}
                        />

                        <SubmitButton
                          ky="submit"
                          //type="primary"
                          htmlType="submit"
                          submitName="Submit"
                          className="defaultbutton"
                          onClick={() => {
                            setFormAction("");
                          }}
                          loading={false}
                        />
                      </div>
                    </div>

                    <Modal
                      title={
                        formAction === "draft" ? "Save as Draft" : "Confirm Expense"
                      }
                      open={isModalVisible}
                      onCancel={handleCancel}
                      className="custom-modal"
                      footer={[
                        <SubmitButton
                          ky="back"
                          type="primary"
                          className="btn px-5 py-2 defaultWhiteBtn rounded-lg"
                          submitName="No"
                          onClick={handleCancel}
                        />,

                        <SubmitButton
                          ky="update"
                          type="primary"
                          htmlType="submit"
                          className="btn px-5 py-2 defaultbutton rounded-lg"
                          submitName="Yes"
                          onClick={handleOk}
                        />,
                      ]}
                    >
                      {formAction === "draft" ? (
                        <p>
                          Please review your entered information before saving the
                          form as a draft. You can make changes later before final
                          submission from <strong>List of Expense</strong>.
                        </p>
                      ) : (
                        <p>
                          Please review your selected or entered information
                          carefully before submitting the form, as changes may not
                          be possible once it's submitted.
                        </p>
                      )}
                    </Modal>
                  </Form>
                </div>
              </>
            }
          </div>
        </div>
      )}
    </>
  );
}
